import { useEffect, useState } from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { FormResponse } from 'components/Form/FormResponse/FormResponse';
import { IconButton } from 'components/IconButton/IconButton';
import { List } from 'components/List/List';

import { useLazyGetCandidateCheckResultQuery } from 'api/candidate-checks-api';
import { useLazyGetFormAnswersForCandidateQuery, useLazyGetFormsByIdQuery } from 'api/forms-api';
import { useLazyGetSubCheckResultQuery } from 'api/sub-checks-api';

export const Questionnaire = ({ check, candidate, setLoading = () => {} }) => {
  const { t } = useTranslation();

  const [getFormsById] = useLazyGetFormsByIdQuery();
  const [getFormAnswersForCandidate] = useLazyGetFormAnswersForCandidateQuery();
  const [getCheckReport] = useLazyGetCandidateCheckResultQuery();
  const [getSubCheckResult] = useLazyGetSubCheckResultQuery();

  const [forms, setForms] = useState([]);

  const [hasSubChecks, setHasSubChecks] = useState(check.sub_checks?.length > 0);

  const [selectedFormId, setSelectedFormId] = useState();
  const [selectedForm, setSelectedForm] = useState();
  const [answers, setAnswers] = useState([]);

  useEffect(() => {
    const hasSubChecks = check.sub_checks?.length > 0;
    setHasSubChecks(hasSubChecks);

    setLoading(true);
    let formIds = [];

    if (hasSubChecks) {
      formIds = check.sub_checks.map(subCheck => subCheck.details.id);
    } else {
      formIds.push(check.details.form_id);
    }

    getFormsById(formIds)
      .unwrap()
      .then(forms => {
        setForms(
          forms.map(form => {
            let completed = false;
            if (hasSubChecks) {
              completed =
                check.sub_checks.find(subCheck => subCheck.details.id == form.id)?.status ==
                'completed';
            } else {
              completed = check.status == 'completed';
            }
            return { ...form, completed };
          }),
        );
        setLoading(false);
      });
  }, [check.id]);

  useEffect(() => {
    if (!selectedFormId) return;

    setLoading(true);
    getFormAnswersForCandidate({ id: selectedFormId, candidateId: candidate.id })
      .unwrap()
      .then(answers => {
        setAnswers(answers);
        setSelectedForm(forms.find(form => form.id == selectedFormId));
        setLoading(false);
      });
  }, [selectedFormId]);

  const download = async () => {
    setLoading(true);
    if (hasSubChecks) {
      const subCheck = check.sub_checks.find(subCheck => subCheck.details.id == selectedFormId);
      await getSubCheckResult({ subCheck, check, candidate }).unwrap();
    } else {
      await getCheckReport({ check, candidate }).unwrap();
    }
    setLoading(false);
  };

  return (
    <div className={cn('u-flex-box', 'u-flex-align-start')}>
      <List
        title={t('check.questionnaire.title')}
        subtitle={t('check.questionnaire.subtitle')}
        className="u-margin-right--large u-flex-align-self-normal">
        <List.Category>
          {forms.map(form => (
            <List.Item
              key={form.id}
              title={form.name}
              value={form.id}
              onClick={setSelectedFormId}
              disabled={!form.completed}
            />
          ))}
        </List.Category>
      </List>
      {selectedForm && (
        <div className={cn('card', 'card-with-border', 'u-min-width-medium', 'u-width-50')}>
          <div className="u-flex-box u-flex-justify-between align-items-center u-margin">
            <h1 className="title-3">{selectedForm.name}</h1>
            <IconButton icon="download" tip={t('common.download')} onClick={download} />
          </div>
          <div className="u-divider u-margin-bottom--large" />
          <div className="u-margin">
            <FormResponse form={selectedForm} answers={answers} />
          </div>
        </div>
      )}
    </div>
  );
};
